import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';

export interface DropdownOption {
  value: any;
  displayName: string;
}

export interface SelectionChangeEvent {
  value: any;
  option: DropdownOption | null;
}

@Component({
  selector: 'single-select-dropdown-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectDropdownListComponent),
      multi: true
    }
  ],
  templateUrl: './single-select-dropdown-list.component.html',
  styles: [`
    .assignment-loading-option {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
  `]
})
export class SingleSelectDropdownListComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() set options(value: DropdownOption[]) { this._options.set(value); }
  get options() { return this._options(); }
  private readonly _options = signal<DropdownOption[]>([]);

  @Input() label: string = '';
  @Input() ariaLabel: string = '';
  @Input() helpText: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() set showEmptyOption(value: boolean) { this._showEmptyOption.set(value); }
  get showEmptyOption() { return this._showEmptyOption(); }
  private readonly _showEmptyOption = signal(false);
  @Input() set emptyOptionText(value: string) { this._emptyOptionText.set(value); }
  get emptyOptionText() { return this._emptyOptionText(); }
  private readonly _emptyOptionText = signal('-- Select --');
  @Input() loading = false;
  @Input() set unresolvedValueText(value: string) { this._unresolvedValueText.set(value); }
  get unresolvedValueText() { return this._unresolvedValueText(); }
  private readonly _unresolvedValueText = signal('');
  @Input() set selectedValue(value: any) { this.writeValue(value); }

  @Output() selectionChange = new EventEmitter<SelectionChangeEvent>();
  @Output() opened = new EventEmitter<void>();

  inputControl = new FormControl();
  readonly hasFocus = signal(false);
  private readonly filterValue = signal<string>('');
  
  private readonly destroy$ = new Subject<void>();
  private onChange = (value: any) => {};
  private onTouched = () => {};
  private readonly currentValue = signal<any>(null);

  // Computed signal for all options including empty option
  allOptions = computed<DropdownOption[]>(() => {
    const options = this._options();
    const emptyOption: DropdownOption = { value: null, displayName: this.emptyOptionText };
    return this.showEmptyOption ? [emptyOption, ...options] : options;
  });

  // Computed signal for filtered options
  filteredOptions = computed<DropdownOption[]>(() => {
    const filter = this.filterValue();
    const options = this.allOptions();
    
    if (!filter || filter.trim() === '') {
      return options;
    }
    
    const filterValueLower = filter.toLowerCase().trim();
    return options.filter(option => 
      option.displayName.toLowerCase().includes(filterValueLower)
    );
  });

  // Compare by content: callers may bind new arrays each cycle, and re-emitting an equivalent option
  // would re-run the effect below, whose async autocomplete write triggers another change detection forever.
  private readonly selectedOption = computed<DropdownOption | null>(() => {
    const value = this.currentValue();
    return this.allOptions().find(option => option.value === value)
      ?? (value && this._unresolvedValueText() ? { value, displayName: this._unresolvedValueText() } : null);
  }, { equal: (a, b) => a === b || (!!a && !!b && a.value === b.value && a.displayName === b.displayName) });

  constructor() {
    // Effect to handle when options change and we need to set a pending value
    effect(() => {
      this.inputControl.setValue(this.selectedOption(), { emitEvent: false });
    });
  }

  ngOnInit() {
    // Subscribe to input control changes for user selections
    this.inputControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (typeof value === 'object' && value !== null) {
        if (this.loading) return;
        this.currentValue.set(value.value);
        // User selected an option from dropdown
        this.onChange(value.value);
        this.selectionChange.emit({ value: value.value, option: value });
      } else if (typeof value === 'string') {
        // User is typing - update filter value
        this.filterValue.set(value);
        // Don't emit selectionChange for typing - only for actual selections
      }
      this.onTouched();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.currentValue.set(value);
    
    this.inputControl.setValue(this.selectedOption(), { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  // Display function for autocomplete
  displayFn = (option: DropdownOption): string => {
    return option ? option.displayName : '';
  };

  inputDisplayValue(): string {
    return typeof this.inputControl.value === 'string'
      ? this.inputControl.value
      : this.displayFn(this.selectedOption());
  }

  onInteraction(): void {
    if (!this.isDisabled) this.opened.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      this.onInteraction();
    }
  }

  get isDisabled(): boolean {
    return this.disabled || this.readonly;
  }

  get showError(): boolean {
    return this.required && this.inputControl.touched && !this.inputControl.value;
  }
}